import { useQuery } from '@tanstack/react-query';
import { tradesKeys } from '@/services/query-keys/trades-keys';
import { tradesService } from '@/services/trades/trades-service';
import type { BenchmarkApiItem, BenchmarkRate } from '@/types';

/**
 * Canonical benchmark order — drives select option order in the UI. tonar/honia
 * are intentionally omitted (LR-443): they are no longer offered in the UI.
 */
export const BENCHMARK_KEYS: BenchmarkRate[] = [
  'sofr',
  'obfr',
  'estr',
  'sonia',
  'other',
];

/** Per-benchmark display metadata for the Rate Details dropdown (LR-443). */
export interface BenchmarkMeta {
  currency: string;
  /** Display-ready Bloomberg ticker, e.g. 'BBG SOFRRATE' (rendered verbatim). */
  bbgTicker: string | null;
}

/**
 * Build a {benchmark → rate%} map from the API list (LR-435). Only the offered
 * benchmarks (`BENCHMARK_KEYS`) are populated — others (tonar/honia) are absent,
 * hence the `Partial` return; the offered ones default to 0 so the form never
 * reads `undefined`. Keys are emitted in canonical order for stable rendering.
 */
export const toBenchmarkRateMap = (
  items?: BenchmarkApiItem[],
): Partial<Record<BenchmarkRate, number>> => {
  const byName = new Map((items ?? []).map((i) => [i.name, i.rate]));
  return BENCHMARK_KEYS.reduce<Partial<Record<BenchmarkRate, number>>>(
    (acc, key) => {
      acc[key] = byName.get(key) ?? 0;
      return acc;
    },
    {},
  );
};

/**
 * Build a {benchmark → {currency, bbgTicker}} map from the API list (LR-443).
 * Drives the currency + Bloomberg ticker shown in the Rate Details dropdown.
 * Only the offered benchmarks (`BENCHMARK_KEYS`) are populated — others
 * (tonar/honia) are absent, hence the `Partial` return; benchmarks absent from
 * the response default to empty currency / null ticker.
 */
export const toBenchmarkMetaMap = (
  items?: BenchmarkApiItem[],
): Partial<Record<BenchmarkRate, BenchmarkMeta>> => {
  const byName = new Map((items ?? []).map((i) => [i.name, i]));
  return BENCHMARK_KEYS.reduce<Partial<Record<BenchmarkRate, BenchmarkMeta>>>(
    (acc, key) => {
      const item = byName.get(key);
      acc[key] = {
        currency: item?.currency_default ?? '',
        bbgTicker: item?.bbg_ticker ?? null,
      };
      return acc;
    },
    {},
  );
};

/**
 * Fetch the benchmark reference rates and cache for 10 minutes. The Trade
 * Entry form consumes these to populate the benchmark rate and "Current value"
 * hint, replacing the hardcoded BENCHMARK_RATES tables.
 */
export const useBenchmarks = (enabled = true) => {
  return useQuery({
    queryKey: tradesKeys.benchmarks(),
    queryFn: () => tradesService.getBenchmarks(),
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};
